import { createContext, useContext, useCallback, useEffect, useRef, useState } from 'react'
import { processDataService } from '../services/processDataService'
import { useAuthContext } from '../hooks/useAuthContext'
import { toast } from 'react-toastify'
import { NUMBER_OF_TASKS_AND_QUERIES_ANSWERED_AT_A_TIME_BY_USER } from '../utility/constants'

const BadgeContext = createContext(null)

export function useBadges() {
  const ctx = useContext(BadgeContext)
  if (!ctx) throw new Error('useBadges must be used inside BadgeProvider')
  return ctx
}

export function BadgeProvider({ children }) {
  const { user } = useAuthContext()
  const [badgeCounts, setBadgeCounts] = useState([])

  const prevBadgeCountsRef = useRef(null)
  const prevTaskIdsRef = useRef(null)

  // Reset diff-tracking refs whenever the logged-in user changes
  useEffect(() => {
    prevTaskIdsRef.current = null
    prevBadgeCountsRef.current = null
  }, [user?.id])

  const fetchBadgeData = useCallback(async () => {
    if (!user) return

    const badgeCount = await processDataService.getBadgeData(user)
    const newQueryCount = parseInt(badgeCount.queryCount) || 0

    const activeIds = new Set(badgeCount.activeTaskIds)
    const selfCreatedIds = new Set(badgeCount.selfCreatedTaskIds)
    const externalIds = new Set([...activeIds].filter((id) => !selfCreatedIds.has(id)))

    const prevIds = prevTaskIdsRef.current

    if (prevIds !== null) {
      const newlyArrivedIds = [...externalIds].filter((id) => !prevIds.has(id))
      const taskDelta = newlyArrivedIds.length

      const prev = prevBadgeCountsRef.current
      const queryDelta = newQueryCount - (prev?.queryCount ?? newQueryCount)

      if (taskDelta > 0) {
        toast.warning(`${taskDelta} new task${taskDelta > 1 ? 's' : ''} available`, {
          position: 'bottom-right',
          autoClose: 4000,
        })
      }
      if (queryDelta > 0) {
        toast.warning(`${queryDelta} new ${queryDelta > 1 ? 'queries' : 'query'} available`, {
          position: 'bottom-right',
          autoClose: 4000,
        })
      }
    }

    prevTaskIdsRef.current = externalIds
    prevBadgeCountsRef.current = { queryCount: newQueryCount }

    setBadgeCounts([
      { text: 'My Tasks', count: activeIds.size },
      { text: 'My Queries', count: newQueryCount },
    ])
  }, [user])

  const decrementQueryBadge = useCallback((amount = 1) => {
    setBadgeCounts((prev) =>
      prev.map((item) =>
        item.text === 'My Queries' ? { ...item, count: Math.max(0, item.count - amount) } : item,
      ),
    )
    if (prevBadgeCountsRef.current) {
      prevBadgeCountsRef.current = {
        ...prevBadgeCountsRef.current,
        queryCount: Math.max(0, (prevBadgeCountsRef.current.queryCount ?? 0) - amount),
      }
    }
  }, [])

  const decrementTaskBadge = useCallback(
    (amount = NUMBER_OF_TASKS_AND_QUERIES_ANSWERED_AT_A_TIME_BY_USER) => {
      setBadgeCounts((prev) =>
        prev.map((item) =>
          item.text === 'My Tasks' ? { ...item, count: Math.max(0, item.count - amount) } : item,
        ),
      )
    },
    [],
  )

  useEffect(() => {
    if (user?.id) {
      fetchBadgeData()
      const id = setInterval(fetchBadgeData, 300000)
      return () => clearInterval(id)
    }
  }, [user, fetchBadgeData])

  return (
    <BadgeContext.Provider
      value={{ badgeCounts, fetchBadgeData, decrementQueryBadge, decrementTaskBadge }}
    >
      {children}
    </BadgeContext.Provider>
  )
}
